/**
 * RallyStream appliance detail — read-only for now.
 */

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import PageHeader from "@/components/PageHeader";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "@/components/StatusStates";
import { getRallystreamDevice } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const RS_SURFACE = "min-h-full bg-[#0F0E0C] text-slate-100";
const RS_PANEL =
  "rounded-lg border border-white/10 bg-black/30 shadow-sm shadow-black/40";
const RS_KV = "text-xs font-mono text-[#4A8FE5] break-all";

export default function StreamDeviceDetail() {
  const { publicId = "" } = useParams<{ publicId: string }>();
  const decoded = decodeURIComponent(publicId);

  const deviceQ = useQuery({
    queryKey: ["rallystream-device", decoded],
    queryFn: () => getRallystreamDevice(decoded),
    enabled: decoded.length > 0,
  });

  return (
    <div className={RS_SURFACE}>
      <div className="mx-auto max-w-4xl px-8 py-8 space-y-6">
        <div className="flex items-center gap-4 text-sm">
          <Link
            to="/stream/devices"
            className="text-[#FB6E2E] hover:underline font-semibold uppercase tracking-wide"
          >
            RallyStream
          </Link>
          <span className="text-slate-600">/</span>
          <span className="text-slate-400 font-mono text-xs">{decoded}</span>
        </div>

        {deviceQ.isLoading ? <LoadingBlock label="Loading appliance" /> : null}
        {deviceQ.isError ? <ErrorBlock error={deviceQ.error} /> : null}

        {deviceQ.isSuccess ? (
          <>
            <PageHeader
              title={deviceQ.data.friendly_label ?? "RallyStream unit"}
              subtitle="Encoder appliance registry row (read-only)."
            />
            <section className={`${RS_PANEL} p-6 space-y-4`}>
              <dl className="grid gap-3 md:grid-cols-2 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Public id
                  </dt>
                  <dd className={RS_KV}>{deviceQ.data.device_public_id}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Internal id
                  </dt>
                  <dd className={RS_KV}>{deviceQ.data.id}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Config version
                  </dt>
                  <dd className="text-slate-200">{deviceQ.data.config_version}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Firmware target
                  </dt>
                  <dd className="text-slate-200">{deviceQ.data.firmware_target}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Last check-in
                  </dt>
                  <dd className="text-slate-200">
                    {deviceQ.data.last_check_in_at
                      ? formatDateTime(deviceQ.data.last_check_in_at)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Customer id
                  </dt>
                  <dd className={RS_KV}>{deviceQ.data.customer_id ?? "—"}</dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Ed25519 public key (base64)
                  </dt>
                  <dd className={`${RS_KV} mt-1`}>
                    {deviceQ.data.ed25519_pubkey_b64}
                  </dd>
                </div>
                <div className="md:col-span-2">
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Last report JSON
                  </dt>
                  <dd className="mt-2 rounded-md bg-black/50 p-3 text-xs font-mono text-slate-300 overflow-x-auto">
                    {deviceQ.data.last_report
                      ? JSON.stringify(deviceQ.data.last_report, null, 2)
                      : "—"}
                  </dd>
                </div>
              </dl>
            </section>
          </>
        ) : null}

        {!deviceQ.isLoading && !deviceQ.isFetching && !decoded ? (
          <EmptyBlock title="Missing device id" description="No public id in URL." />
        ) : null}
      </div>
    </div>
  );
}
